<template>
  <div class="wallet-form__amount-group">
    <div class="wallet-form__amount-row">
      <slot />
      <div
        v-if="mode === 'withdraw'"
        class="wallet-form__usdt-stack"
      >
        <aside
          class="wallet-form__usdt-inline"
          :class="{ 'wallet-form__usdt-inline--empty': krwAmount <= 0 && !rateLoading }"
        >
          <span class="wallet-form__usdt-inline-label">≈</span>
          <span class="wallet-form__usdt-inline-body">
            <template v-if="krwAmount > 0">
              <template v-if="showLoadingPlaceholder">
                <span class="wallet-form__usdt-inline-value">…</span>
              </template>
              <template v-else-if="displayUsdt != null">
                <strong
                  class="wallet-form__usdt-inline-value"
                  :title="formattedDisplayFull"
                >{{ formattedDisplay }}</strong>
              </template>
              <template v-else-if="rateError">
                <span class="wallet-form__usdt-inline-value wallet-form__usdt-inline-value--error">—</span>
              </template>
            </template>
            <template v-else>
              <span class="wallet-form__usdt-inline-value">—</span>
            </template>
          </span>
          <span class="wallet-form__usdt-inline-unit">USDT</span>
        </aside>
        <p v-if="showFeeSummary" class="wallet-usdt-fee-summary wallet-usdt-fee-summary--below">
          <template v-if="feeReady && feeUsdt != null">
            <span class="wallet-usdt-fee-summary__chip">
              <em>{{ $t('wallet.networkFeeSimpleRequest') }}</em>
              <strong>{{ formattedBaseUsdt }}</strong>
            </span>
            <span class="wallet-usdt-fee-summary__chip wallet-usdt-fee-summary__chip--fee">
              <em>{{ $t('wallet.networkFeeSimpleFee') }}</em>
              <strong>{{ formattedFeeUsdt }}</strong>
            </span>
          </template>
          <span v-else class="wallet-usdt-fee-summary__loading">{{ $t('wallet.networkFeeSimpleLoading') }}</span>
        </p>
      </div>
      <aside
        v-else
        class="wallet-form__usdt-inline"
        :class="{ 'wallet-form__usdt-inline--empty': krwAmount <= 0 && !rateLoading }"
      >
        <span class="wallet-form__usdt-inline-label">≈</span>
        <span class="wallet-form__usdt-inline-body">
          <template v-if="krwAmount > 0">
            <template v-if="showLoadingPlaceholder">
              <span class="wallet-form__usdt-inline-value">…</span>
            </template>
            <template v-else-if="displayUsdt != null">
              <strong
                class="wallet-form__usdt-inline-value"
                :title="formattedDisplayFull"
              >{{ formattedDisplay }}</strong>
            </template>
            <template v-else-if="rateError">
              <span class="wallet-form__usdt-inline-value wallet-form__usdt-inline-value--error">—</span>
            </template>
          </template>
          <template v-else>
            <span class="wallet-form__usdt-inline-value">—</span>
          </template>
        </span>
        <span class="wallet-form__usdt-inline-unit">USDT</span>
      </aside>
    </div>

    <p v-if="rate" class="wallet-form__usdt-rate">
      {{ $t(rateLabelKey, { rate: formattedRate }) }}
      <span class="wallet-form__usdt-rate-hint">{{ $t('wallet.rateLive') }}</span>
    </p>
    <p v-else-if="rateError && krwAmount > 0" class="wallet-form__usdt-rate wallet-form__usdt-rate--error">
      {{ rateError }}
    </p>

    <slot name="addons" />
  </div>
</template>

<script>
import { subscribeUsdtFiatRate } from '@/services/usdtRate'
import { subscribeTronNetworkFee } from '@/services/tronNetworkFee'
import { convertFiatToUsdt, formatUsdtAmount } from '@/utils/format'
import { applyWithdrawUsdtFee } from '@/utils/usdtNetworkFee'

export default {
  name: 'WalletUsdtQuote',
  props: {
    krwAmount: {
      type: Number,
      default: 0
    },
    mode: {
      type: String,
      default: 'deposit',
      validator: value => ['deposit', 'withdraw'].includes(value)
    }
  },
  data () {
    return {
      rate: null,
      rateLoading: false,
      rateError: '',
      rateSource: '',
      feeUsdt: null,
      feeTrx: null,
      feeLoading: false,
      feeReady: false
    }
  },
  computed: {
    rateLabelKey () {
      if (this.rateSource === 'bithumb' && this.$te('wallet.rateLabelBithumb')) {
        return 'wallet.rateLabelBithumb'
      }
      if (this.rateSource === 'usd' && this.$te('wallet.rateLabelUsd')) {
        return 'wallet.rateLabelUsd'
      }
      return 'wallet.rateLabel'
    },
    showFeeSummary () {
      return this.mode === 'withdraw' && this.krwAmount > 0 && !!this.rate
    },
    showLoadingPlaceholder () {
      if (this.rateLoading && this.baseUsdt == null) return true
      if (this.mode !== 'withdraw') return false
      if (!this.feeReady) return this.krwAmount > 0 && !!this.rate
      return false
    },
    baseUsdt () {
      return convertFiatToUsdt(this.krwAmount, this.rate)
    },
    displayUsdt () {
      if (this.baseUsdt == null) return null
      if (this.mode !== 'withdraw') return this.baseUsdt
      if (!this.feeReady || this.feeUsdt == null) return null
      return applyWithdrawUsdtFee(this.baseUsdt, this.feeUsdt)
    },
    formattedBaseUsdt () {
      if (this.baseUsdt == null) return '—'
      return formatUsdtAmount(this.baseUsdt, { maxDecimals: 2, minDecimals: 2 })
    },
    formattedDisplay () {
      if (this.displayUsdt == null) return '—'
      return formatUsdtAmount(this.displayUsdt, { maxDecimals: 2, minDecimals: 2 })
    },
    formattedDisplayFull () {
      if (this.displayUsdt == null) return ''
      return formatUsdtAmount(this.displayUsdt, { maxDecimals: 4, minDecimals: 2 })
    },
    formattedFeeUsdt () {
      if (this.feeUsdt == null) return '—'
      return formatUsdtAmount(this.feeUsdt, { maxDecimals: 2, minDecimals: 2 })
    },
    formattedFeeTrx () {
      if (this.feeTrx == null) return '—'
      return formatUsdtAmount(this.feeTrx, { maxDecimals: 2, minDecimals: 2 })
    },
    formattedRate () {
      if (!this.rate) return ''
      return this.formatCurrency(this.rate)
    }
  },
  watch: {
    siteCurrency: {
      immediate: true,
      handler () {
        this.resubscribeRate()
      }
    },
    displayUsdt () {
      this.emitQuoteChange()
    },
    feeUsdt () {
      this.emitQuoteChange()
    },
    feeReady () {
      this.emitQuoteChange()
    },
    feeLoading () {
      this.emitQuoteChange()
    }
  },
  methods: {
    emitQuoteChange () {
      this.$emit('quote-change', {
        baseUsdt: this.baseUsdt,
        feeUsdt: this.feeUsdt,
        feeTrx: this.feeTrx,
        feeLoading: this.feeLoading,
        feeReady: this.feeReady,
        totalUsdt: this.displayUsdt
      })
    },
    resubscribeRate () {
      if (this._unsubscribeRate) {
        this._unsubscribeRate()
        this._unsubscribeRate = null
      }
      this.rate = null
      this.rateLoading = true
      this.rateError = ''
      this.rateSource = ''
      this._unsubscribeRate = subscribeUsdtFiatRate(this.siteCurrency, (state) => {
        this.rate = state.rate
        this.rateLoading = state.loading
        this.rateError = state.error
        this.rateSource = state.source || ''
      })
    }
  },
  mounted () {
    this.resubscribeRate()
    if (this.mode === 'withdraw') {
      this._unsubscribeFee = subscribeTronNetworkFee((state) => {
        this.feeUsdt = state.feeUsdt
        this.feeTrx = state.feeTrx
        this.feeLoading = state.loading
        this.feeReady = state.ready === true
      })
    }
    this.emitQuoteChange()
  },
  beforeDestroy () {
    if (this._unsubscribeRate) this._unsubscribeRate()
    if (this._unsubscribeFee) this._unsubscribeFee()
  }
}
</script>
